import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CarouselHero from '@/components/base/CarouselHero';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useCollection } from '@/hooks/useCollection';
import { CHURCH_WORSHIP_PHOTOS, CHURCH_MISSION_PHOTO, CHURCH_LEGACY_PHOTO } from '@/lib/churchPhotos';
import type { Leader, GalleryImage } from '@/lib/types';

function LeaderCard({ leader }: { leader: Leader }) {
  return (
    <article className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all">
      <div className="h-72 overflow-hidden">
        <img
          src={leader.image}
          alt={leader.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="w-8 h-1 bg-accent-500 mb-3" />
        <h3 className="font-heading font-semibold text-lg text-foreground-800 leading-tight mb-1">{leader.name}</h3>
        <p className="text-accent-600 text-sm font-label font-medium mb-3">{leader.title}</p>
        <p className="text-foreground-500 text-sm leading-relaxed line-clamp-3">{leader.bio}</p>
      </div>
    </article>
  );
}

export default function AboutPage() {
  const { data: settings } = useSiteSettings();
  const { rows: leaders } = useCollection<Leader>('leaders', 'sort_order', true);
  const { rows: galleryImages } = useCollection<GalleryImage>('gallery_images', 'sort_order', true);
  const highlights = galleryImages.filter((img) => img.is_published !== false).slice(0, 12);

  useEffect(() => {
    document.title = 'About TGMCI — Our Mission, Vision & Leadership';
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const impactStats = settings?.impact_stats?.length
    ? settings.impact_stats
    : [
        { label: 'Lives Transformed', value: 500, suffix: '+' },
        { label: 'Meals Distributed', value: 2000, suffix: '+' },
        { label: 'Students Sponsored', value: 150, suffix: '+' },
        { label: 'Homes Built', value: 30, suffix: '+' },
      ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <CarouselHero
          title="Empowering Communities Through Faith and Service"
          subtitle="Discover our church's mission, vision, values, and the leaders who guide us."
          images={CHURCH_WORSHIP_PHOTOS}
          breadcrumb="About"
        />

        {/* Mission/Vision Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
              <div className="reveal-left">
                <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Who We Are</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-6">
                  Discover Our Church&apos;s Mission, Vision, and Values
                </h2>
                <div className="w-12 h-1 bg-accent-500 mb-6" />
                <p className="text-foreground-600 text-base leading-relaxed mb-8">
                  At <strong>{settings?.church_name || 'TGMCI'}</strong>, we are dedicated to empowering youth and serving our community through faith-based initiatives and outreach programs. Our church is a family united by love, purpose, and the transformative power of faith.
                </p>

                <div className="space-y-5">
                  {[
                    { icon: 'ri-flashlight-line', title: 'Our Mission', text: settings?.mission },
                    { icon: 'ri-eye-line', title: 'Our Vision', text: settings?.vision },
                    { icon: 'ri-shield-star-line', title: 'Core Values', text: settings?.core_values },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4 p-4 bg-background-100 rounded-xl border border-background-200">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-600 text-white shrink-0">
                        <i className={`${item.icon} text-lg`} aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground-800 mb-1">{item.title}</h3>
                        <p className="text-foreground-500 text-sm leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — image collage */}
              <div className="reveal relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="img-zoom rounded-xl overflow-hidden h-56">
                    <img
                      src={CHURCH_MISSION_PHOTO}
                      alt="Ministry at the altar"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="img-zoom rounded-xl overflow-hidden h-56 mt-10">
                    <img
                      src={CHURCH_WORSHIP_PHOTOS[1]}
                      alt="Praise and worship"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="img-zoom rounded-xl overflow-hidden h-48">
                    <img
                      src={CHURCH_WORSHIP_PHOTOS[0]}
                      alt="Congregation in worship"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="img-zoom rounded-xl overflow-hidden h-48 -mt-4">
                    <img
                      src={CHURCH_WORSHIP_PHOTOS[2]}
                      alt="Church fellowship"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-primary-600 text-white rounded-xl p-5">
                  <div className="text-3xl font-heading font-bold">10+</div>
                  <div className="text-xs font-label text-white/80 mt-1 uppercase tracking-wide">Years of Ministry</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Legacy Banner */}
        <section className="relative py-20 bg-background-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={CHURCH_LEGACY_PHOTO}
              alt="Church ministry"
              className="w-full h-full object-cover object-top opacity-20"
            />
          </div>
          <div className="relative z-10 max-w-[900px] mx-auto px-4 md:px-8 text-center">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-4">A Legacy of Service</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
              A Legacy of Service and Faith
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-4">
              Dedicated to empowering lives, our church fosters hope, compassion, and unity among all members of the community.
            </p>
            <p className="text-accent-400 font-heading text-xl italic">&ldquo;{settings?.scripture || 'Colossians 1:13'}&rdquo;</p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-primary-600">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {impactStats.map((stat) => (
                <div key={stat.label}>
                  <div className="font-heading text-4xl md:text-5xl font-bold text-white">
                    {stat.value.toLocaleString()}{stat.suffix}
                  </div>
                  <p className="text-white/70 font-label text-sm uppercase tracking-wide mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Unique Approach */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Our Approach</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
                Our Unique Approach to Faith &amp; Community
              </h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
              <p className="text-foreground-500 mt-5 text-base leading-relaxed">
                We believe in fostering a nurturing environment that encourages growth, community engagement, and active participation in our ministries and events.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: 'ri-user-star-line', title: 'Youth-Centered', text: 'Every program we run is designed to empower the next generation with skills, faith, and purpose.' },
                { icon: 'ri-community-line', title: 'Community-Driven', text: 'Our church is rooted in the community, serving the practical and spiritual needs of those around us.' },
                { icon: 'ri-globe-line', title: 'Internationally Connected', text: 'With branches in Cameroon, Ghana, and connections in the USA, our impact crosses borders and cultures.' },
              ].map((item, idx) => (
                <div key={item.title} className="reveal p-7 bg-background-50 rounded-xl border border-background-200 text-center" style={{ transitionDelay: `${idx * 100}ms` }}>
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mx-auto mb-4">
                    <i className={`${item.icon} text-2xl`} aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-3">{item.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="text-center mb-14 reveal">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Meet Our Team</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
                Our Ministers &amp; Leaders
              </h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
              <p className="text-foreground-500 mt-5 text-base max-w-xl mx-auto">
                Faithful shepherds dedicated to guiding TGMCI with wisdom, love, and an unwavering commitment to the community.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {leaders.map((leader) => (
                <LeaderCard key={leader.id} leader={leader} />
              ))}
            </div>
          </div>
        </section>

        {/* Photo Carousel — Ministry Highlights */}
        <section className="py-16 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">Highlights of Our Great Ministry</h2>
              <div className="w-12 h-1 bg-accent-500 mt-4 mx-auto" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {highlights.map((img) => (
                <Link
                  key={img.id}
                  to="/highlights"
                  className="img-zoom rounded-lg overflow-hidden h-40 md:h-44 relative group"
                >
                  <img
                    src={img.src}
                    alt={img.title || img.alt || 'TGMCI ministry highlight'}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs font-medium line-clamp-1">
                      {img.title || img.alt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/highlights" className="btn-outline">
                <i className="ri-image-line" aria-hidden="true" />
                View Full Gallery
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Stay Connected with Our Community</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">Subscribe to our newsletter for updates and events.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-accent">
              <i className="ri-user-add-line" aria-hidden="true" />
              Get Involved
            </Link>
            <Link to="/give" className="btn-outline-white">
              <i className="ri-heart-line" aria-hidden="true" />
              Support TGMCI
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}